import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import flatMap from 'lodash/flatMap';
import QueryString from 'qs';

import { API_TEAM_USERS, buildApiGetUserDetails } from 'constants/api-paths';
import AxiosClient from 'configurations/api-client';

const API_MEMBERSHIP_TAGS = '/membership_tags';

export const useFilterMembershipTag = (siteId, membershipTag) => {
  const query = useInfiniteQuery({
    queryKey: ['teamMembers', siteId, membershipTag],
    queryFn: async ({ signal, pageParam = 1 }) => {
      const response = await AxiosClient.get(API_TEAM_USERS, {
        params: {
          siteId,
          page: pageParam,
          perPage: 9,
          membershipTag,
          orderBy: 'name',
          sortingOrder: 'asc',
        },
        paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: 'brackets' }),
        signal,
      });

      return get(response, 'data', {});
    },
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!siteId && !isEmpty(membershipTag),
    refetchOnWindowFocus: false,
    staleTime: 5 * 1000 * 60,
  });

  const { data } = query;
  const teamMembers = useMemo(() => flatMap(data?.pages, 'data'), [data]);

  return { ...query, teamMembers };
};

export const useGetMember = (siteId, memberSlug) =>
  useQuery({
    queryKey: ['member', siteId, memberSlug],
    queryFn: async ({ signal }) => {
      const response = await AxiosClient.get(buildApiGetUserDetails(memberSlug), {
        params: { siteId },
        signal,
      });

      return get(response, 'data', {});
    },
    enabled: !!siteId && !isEmpty(memberSlug),
    refetchOnWindowFocus: false,
  });

export const useGetMembershipTags = (siteId) => {
  const { data: membershipTags = [], isLoading } = useQuery({
    queryKey: ['membershipTags', siteId],
    queryFn: async ({ signal }) => {
      const response = await AxiosClient.get(API_MEMBERSHIP_TAGS, {
        params: { siteId },
        signal,
      });

      return get(response, 'data', []);
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
  });

  return { membershipTags, isLoading };
};
